/**
 * Decrypts identify payload from GET /api/vertriebsportal/hubspot-contact-email
 * when the server returns `{ v: 1, d: "..." }` (AES-256-GCM).
 * Must match `packages/server/src/utils/hubspot-identify-payload-crypto.ts`.
 */
const base64UrlToUint8Array = (s: string): Uint8Array => {
    const pad = "==".slice((s.length + 3) % 4);
    const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + pad;
    const binary = atob(b64);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        out[i] = binary.charCodeAt(i);
    }
    return out;
};

export type HubspotIdentifyPayload = {
    email: string;
    firstname?: string;
    lastname?: string;
};

export const decryptHubspotIdentifyPayloadJson = async (secret: string, d: string): Promise<HubspotIdentifyPayload> => {
    const keyRaw = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(secret));
    const key = await crypto.subtle.importKey("raw", keyRaw, { name: "AES-GCM" }, false, ["decrypt"]);

    const combined = base64UrlToUint8Array(d);
    if (combined.length < 12 + 16 + 1) {
        throw new Error("Invalid encrypted payload length");
    }

    const iv = combined.slice(0, 12);
    const tag = combined.slice(-16);
    const ciphertext = combined.slice(12, -16);
    const ciphertextWithTag = new Uint8Array(ciphertext.length + tag.length);
    ciphertextWithTag.set(ciphertext, 0);
    ciphertextWithTag.set(tag, ciphertext.length);

    const decrypted = await crypto.subtle.decrypt({ name: "AES-GCM", iv, tagLength: 128 }, key, ciphertextWithTag);

    const parsed = JSON.parse(new TextDecoder().decode(decrypted)) as unknown;
    if (!parsed || typeof parsed !== "object") {
        throw new Error("Invalid decrypted payload");
    }
    const o = parsed as Record<string, unknown>;
    if (typeof o.email !== "string") {
        throw new Error("Missing email in decrypted payload");
    }
    return {
        email: o.email,
        ...(typeof o.firstname === "string" ? { firstname: o.firstname } : {}),
        ...(typeof o.lastname === "string" ? { lastname: o.lastname } : {}),
    };
};
